import * as React from "react";
import { Link } from "react-router-dom"
import ChangePasswordInput from "../../core/ChangePasswordInput/ChangePasswordInput";
import Spinner from "../../core/Spinner/Spinner";
import debounce from "../../helpers/debounce";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IPasswordIsValidResponse from "../../types/IPasswordIsValidResponse";
import IPasswordRequirement from "../../types/IPasswordRequirement";
import IResetPasswordResponse from "../../types/IResetPasswordResponse";
import IResponse from "../../types/IResponse";
import IValidateRegistrationKeyResponse from "../../types/IValidateResetPasswordKeyResponse";

enum ProcessStateValues {
    validating,
    resetPassword,
    resetPasswordComplete,
    codeInvalid,
    error
}

interface IResetPasswordProps extends ITranslationProps {
    // onSubmit: (email: string) => void;
    validateResetCode: () => Promise<IResponse<IValidateRegistrationKeyResponse> | null>;
    resetPassword: (password: string, confirmPassword: string, resetCode: string) => Promise<IResponse<IResetPasswordResponse> | null>;
    validatePasswordRequirements(password: string): Promise<IResponse<IPasswordIsValidResponse> | null>;
}

interface IResetPasswordState {
    // state control
    processState: ProcessStateValues,
    processing?: boolean;
    validated: boolean,

    // response from validating the key
    passwordRequirements: IPasswordRequirement[],
    firstName: string,
    email: string,
    resetCode: string,

    // values entered by the user
    password: string,
    confirmPassword: string;

    // error messages
    isPasswordInvalid: boolean,
    isConfirmPasswordInvalid: boolean;
    areFieldInvalid: boolean;
    isResetPasswordFailure: boolean,
}

class ResetPassword extends React.Component<IResetPasswordProps, IResetPasswordState> {
    // We are making an API call to verify the password is correct.  But we don't want to make
    // this call on every key press.  So we are using a debounce function to only make the call
    // after the user has stopped typing for a certain period of time.
    // https://www.carlrippon.com/search-after-stop-typing-react-component/
    private validatePasswordWhenUserStoppedTyping = debounce(
        async () => {
            // call the web service to see if the password meets the requirements
            // update the display to show the user which requirements are not met
            const passwordRequirements = await this.props.validatePasswordRequirements(this.state.password);
            if (passwordRequirements && passwordRequirements.success) {
                this.setState({passwordRequirements: passwordRequirements.payload.requirements});
            }
        },  
        500);
    
    constructor(props: IResetPasswordProps) {
        super(props);

        this.state = { 
            processState: ProcessStateValues.validating,
            processing: false,
            validated: false,
            passwordRequirements: [],
            firstName: "",
            email: "",
            resetCode: "",
            password: "",
            confirmPassword: "",
            isPasswordInvalid: false,
            isConfirmPasswordInvalid: false,
            areFieldInvalid: false,
            isResetPasswordFailure: false,
        };

        // From the ChangePasswordInput control
        this.onChangePasswordInputSubmit = this.onChangePasswordInputSubmit.bind(this);
        this.onPasswordChanged = this.onPasswordChanged.bind(this);

        // Local events
        this.onPasswordChanged = this.onPasswordChanged.bind(this);
        this.inputKeypress = this.inputKeypress.bind(this);
        this.onSubmitPasswordChange = this.onSubmitPasswordChange.bind(this);
    }

    public render() {
        return (
            <div>
                {this.state.processing && <Spinner />}
                <h2>{this.props.t("ResetPassword.Title")}</h2>
                {this.getContent()}
            </div>
        );
    }

    public async componentDidMount() {
        this.setState({ processing: true });
        const response = await this.props.validateResetCode();

        let processState: ProcessStateValues;
        if (response && response.success) {
            if (response.payload.isValid) {
                this.setState({
                    passwordRequirements: response.payload.passwordRequirements,
                    firstName: response.payload.firstName,
                    email: response.payload.email,
                    resetCode: response.payload.resetCode
                });
                processState = ProcessStateValues.resetPassword;
            } else {
                processState = ProcessStateValues.codeInvalid;
            }
        } else {
            processState = ProcessStateValues.error;
        }
        this.setState({ processing: false, processState });    
    }
    
    private getContent() {
        switch(this.state.processState)
        {
            case ProcessStateValues.validating:
                return (
                    <div>{this.props.t("ResetPassword.WaitWhileValidating")}</div>
                );

            case ProcessStateValues.resetPassword:
                return (
                    <div>
                        <div className="mb-3 mt-3">{this.state.firstName}{this.props.t("ResetPassword.Instructions")}</div>
                        <ChangePasswordInput 
                            passwordRequirements={this.state.passwordRequirements}
                            onSubmit={this.onChangePasswordInputSubmit} 
                            onPasswordChanged={this.onPasswordChanged}
                            doValidation={this.state.validated}
                            autoFocus={true}
                        />
                        {this.state.isPasswordInvalid &&
                            <div className="alert alert-warning mt-3">
                                {this.props.t("ResetPassword.PasswordIsInvalid")}
                            </div>
                        }
                        <button type="button" className="btn btn-primary" onClick={this.onSubmitPasswordChange}>{this.props.t("ResetPassword.ResetPasswordButton")}</button>
                        <div className="mt-3">
                        {this.state.areFieldInvalid &&
                                        <div className="alert alert-warning">
                                            {this.props.t("ResetPassword.FieldsInvalid")}
                                        </div>
                                    }
                        
                            {this.state.isResetPasswordFailure &&
                                        <div className="alert alert-warning">
                                            {this.props.t("Global.UnexpectedError")}
                                        </div>
                                    }
                        </div>
                    </div>
                );

            case ProcessStateValues.codeInvalid:
            return (
                <div className="alert alert-warning">
                    {this.props.t("ResetPassword.InvalidCode")}<br /><br />
                    <Link to="/forgotPassword">{this.props.t("ResetPassword.RequestRegistrationLink")}</Link>
                </div>
            );
            
            case ProcessStateValues.error:
                return (
                    <div className="alert alert-warning">
                        {this.props.t("Global.UnexpectedError")}<br /><br />
                        <Link to="/forgotPassword">{this.props.t("ResetPassword.RequestRegistrationLink")}</Link>
                    </div>
                );

            case ProcessStateValues.resetPasswordComplete:
                return (
                    <div>
                        <div>{this.props.t("ResetPassword.ResetPasswordComplete")}</div>
                        <div>{this.props.t("ResetPassword.ResetPasswordEmailSent")} {this.state.email}</div>
                        <div>
                            <Link to="/dashboard">{this.props.t("Global.ContinueButton")}</Link>
                        </div>
                    </div>
                );

            default:
                return;
        }
    }

    // -----------------------------------------------------------
    // Events
    // -----------------------------------------------------------

    private inputKeypress(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter") {
            event.preventDefault();
            this.onSubmitPasswordChange();
        }
    }

    private onSubmitPasswordChange(): void {
        this.setState({ validated: true, processing: true }, async () => {
            if (this.isValid()) {
                // Submit registration request
                const result = await this.props.resetPassword(this.state.password, this.state.password, this.state.resetCode);
                if (result && result.success && result.payload) {
                    if (result.payload.success) {
                        // resetPassword valid and complete
                        this.setState({processState: ProcessStateValues.resetPasswordComplete});
                    } else {
                        this.setState({
                            isPasswordInvalid: result.payload.passwordInvalid,
                            passwordRequirements: result.payload.passwordRequirements,
                            isResetPasswordFailure: false,
                            areFieldInvalid: true,
                        });
                    }
                } else {
                    this.setState({isResetPasswordFailure: true, areFieldInvalid: false});
                }
            } else {
                this.setState({ areFieldInvalid: true});
            }
            this.setState({ processing: false });
        });
   }

    private isValid(): boolean {
        let isValid = true;

        if (this.state.password !== this.state.confirmPassword) {
            isValid = false;
        }

        return isValid;
    }

    // -----------------------------------------------------------
    // ChangePasswordInput control callback functions
    // -----------------------------------------------------------

    // Called if user presses enter on the ChangePasswordInput control
    private onChangePasswordInputSubmit(password: string, confirmPassword: string) {
        this.setState({password, confirmPassword}, () => {
            this.onSubmitPasswordChange();
        });
    }

    private async onPasswordChanged(password: string, confirmPassword: string) {
        this.setState({password, confirmPassword}, () => {
            this.validatePasswordWhenUserStoppedTyping();
        });
    }

}

export default translateComponent(ResetPassword);



// WEBPACK FOOTER //
// ./src/components/ResetPassword/ResetPassword.tsx